import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, BookOpen, Copy, Trash2, Edit, Hash } from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  { value: 'all', label: 'Toate' },
  { value: 'calificare', label: 'Calificare' },
  { value: 'tehnic', label: 'Tehnic' },
  { value: 'financiar', label: 'Financiar' },
  { value: 'experienta', label: 'Experienta' },
  { value: 'resurse', label: 'Resurse Umane' },
  { value: 'general', label: 'General' },
];

const emptyForm = { title: '', content: '', category: 'general', tags: [] };

export default function ResponseLibraryPage() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState('');

  const load = () => {
    API.get('/responses', { params: { category: category !== 'all' ? category : undefined, search: search || undefined } })
      .then(r => setResponses(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [category, search]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Titlu si continut sunt obligatorii'); return; }
    try {
      if (editId) {
        await API.put(`/responses/${editId}`, form);
        toast.success('Raspuns actualizat');
      } else {
        await API.post('/responses', form);
        toast.success('Raspuns salvat');
      }
      setForm(emptyForm); setEditId(null); setOpen(false); load();
    } catch { toast.error('Eroare la salvare'); }
  };

  const handleEdit = (resp) => {
    setForm({ title: resp.title, content: resp.content, category: resp.category, tags: resp.tags || [] });
    setEditId(resp.id); setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Stergi acest raspuns?')) return;
    try { await API.delete(`/responses/${id}`); toast.success('Sters'); load(); } catch { toast.error('Eroare'); }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copiat in clipboard');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  return (
    <div className="p-8" data-testid="response-library-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Biblioteca de Raspunsuri</h1>
          <p className="text-sm text-muted-foreground mt-1">Raspunsuri reutilizabile pentru licitatii</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="create-response-btn"><Plus className="w-4 h-4 mr-2" />Raspuns Nou</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>{editId ? 'Editeaza Raspuns' : 'Raspuns Nou'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Titlu *</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="Ex: Experienta similara constructii" className="mt-1.5 bg-zinc-900/50 border-white/10" data-testid="response-title-input" />
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1.5 bg-zinc-900/50 border-white/10" data-testid="response-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.value !== 'all').map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Continut *</Label>
                <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})}
                  placeholder="Textul raspunsului reutilizabil..." className="mt-1.5 bg-zinc-900/50 border-white/10 min-h-[120px]" data-testid="response-content-input" />
              </div>
              <div>
                <Label>Etichete</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Adauga eticheta"
                    className="bg-zinc-900/50 border-white/10" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} />
                  <Button variant="outline" size="sm" onClick={handleAddTag} type="button">+</Button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.tags.map((t, i) => (
                      <Badge key={i} variant="secondary" className="text-xs cursor-pointer hover:bg-rose-500/10 hover:text-rose-400"
                        onClick={() => setForm({...form, tags: form.tags.filter((_, j) => j !== i)})}>
                        {t} x
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleSave} className="w-full" data-testid="save-response-btn">
                {editId ? 'Actualizeaza' : 'Salveaza Raspuns'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cauta raspunsuri..."
            className="pl-10 bg-zinc-900/50 border-white/10" data-testid="response-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48 bg-zinc-900/50 border-white/10" data-testid="response-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-40 bg-white/5 animate-pulse rounded-lg" />)}
        </div>
      ) : responses.length === 0 ? (
        <Card className="bg-card border-white/10 border-dashed">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground mb-2">{search ? 'Niciun rezultat gasit' : 'Niciun raspuns salvat'}</p>
            <Button onClick={() => setOpen(true)} variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />Adauga primul raspuns</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {responses.map(resp => (
            <Card key={resp.id} className="bg-card border-white/10 hover:border-white/20 transition-all group" data-testid={`response-card-${resp.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>{resp.title}</h3>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleCopy(resp.content)} className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground" data-testid={`copy-response-${resp.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleEdit(resp)} className="p-1.5 rounded hover:bg-white/10 text-muted-foreground hover:text-foreground" data-testid={`edit-response-${resp.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(resp.id)} className="p-1.5 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400" data-testid={`delete-response-${resp.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary ring-primary/20 ring-1 ring-inset border-0">
                    {categories.find(c => c.value === resp.category)?.label || resp.category}
                  </Badge>
                  {resp.usage_count > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Hash className="w-3 h-3" />{resp.usage_count} utilizari</span>}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-4 leading-relaxed">{resp.content}</p>
                {resp.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {resp.tags.map((t, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{t}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
